/**
 * SecurityPage — Privacy & beveiliging (spec §8.8).
 *
 * - Change password through useAuth().updatePassword with the shared client
 *   policy validation (min 8 chars, letter + digit) for fast inline feedback;
 *   the server policy stays authoritative and server errors surface honestly.
 * - Active session info comes from useAuth() (email + role from the server
 *   profile — never localStorage).
 * - Re-authentication note + link to the forgot/reset flow.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Banner, Button, Card, PasswordField, StatusChip, useToast } from '@/components/ui'
import { LockIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { useAuth } from '@/features/auth'
import {
  PASSWORD_POLICY_HINT,
  validatePassword,
  validatePasswordConfirm,
} from '@/features/auth/validation'
import { isAppError } from '@/lib/supabase/errors'
import { PATHS } from '@/app/router/paths'
import './settings.css'

const ROLE_LABEL: Record<string, string> = {
  client: 'Cliënt',
  caregiver: 'Begeleider',
  admin: 'Beheerder',
}

export default function SecurityPage() {
  const { email, role, updatePassword } = useAuth()
  const { showToast } = useToast()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    const passwordValidation = validatePassword(password)
    const confirmValidation = validatePasswordConfirm(password, confirm)
    setPasswordError(passwordValidation)
    setConfirmError(confirmValidation)
    setSubmitError(null)
    if (passwordValidation || confirmValidation) return

    setSubmitting(true)
    try {
      await updatePassword(password)
      setPassword('')
      setConfirm('')
      showToast({ message: 'Je wachtwoord is gewijzigd.', tone: 'success' })
    } catch (error) {
      setSubmitError(
        isAppError(error)
          ? error.userMessage
          : 'Je wachtwoord kon niet worden gewijzigd. Probeer het opnieuw.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="settings-page">
      <PageHeader title="Privacy &amp; beveiliging" backTo={PATHS.app.more} />

      {/* Active session */}
      <section className="settings-section" aria-label="Actieve sessie">
        <h2 className="settings-section__title">Actieve sessie</h2>
        <Card>
          <div className="storage-status">
            <div className="storage-status__row">
              <span className="storage-status__label">Ingelogd als</span>
              <span className="storage-status__value">{email ?? 'Onbekend'}</span>
            </div>
            {role && (
              <div className="storage-status__row">
                <span className="storage-status__label">Rol</span>
                <span className="storage-status__value">
                  <StatusChip status="neutral">{ROLE_LABEL[role] ?? role}</StatusChip>
                </span>
              </div>
            )}
            <div className="storage-status__row">
              <span className="storage-status__label">Sessie</span>
              <span className="storage-status__value">Actief op dit apparaat</span>
            </div>
          </div>
        </Card>
      </section>

      {/* Change password */}
      <section className="settings-section" aria-label="Wachtwoord wijzigen">
        <h2 className="settings-section__title">Wachtwoord wijzigen</h2>
        <Card>
          <div className="settings-stack">
            <PasswordField
              label="Nieuw wachtwoord"
              value={password}
              onChange={(value) => {
                setPassword(value)
                if (passwordError) setPasswordError(validatePassword(value))
              }}
              error={passwordError ?? undefined}
              hint={PASSWORD_POLICY_HINT}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
            <PasswordField
              label="Herhaal nieuw wachtwoord"
              value={confirm}
              onChange={(value) => {
                setConfirm(value)
                if (confirmError) setConfirmError(validatePasswordConfirm(password, value))
              }}
              error={confirmError ?? undefined}
              autoComplete="new-password"
              required
              disabled={submitting}
            />
            {submitError && (
              <p className="settings-inline-error" role="alert">
                {submitError}
              </p>
            )}
            <Button
              onClick={() => void handleSubmit()}
              loading={submitting}
              disabled={password.length === 0 || confirm.length === 0}
              fullWidth
            >
              Wachtwoord wijzigen
            </Button>
          </div>
        </Card>
        <Banner tone="info">
          Om je wachtwoord te wijzigen moet je recent zijn ingelogd. Lukt het niet, log dan opnieuw
          in en probeer het nog een keer.
        </Banner>
      </section>

      {/* Reset link */}
      <section className="settings-section" aria-label="Wachtwoord vergeten">
        <h2 className="settings-section__title">Wachtwoord vergeten?</h2>
        <Card>
          <div className="settings-stack">
            <p className="a11y-preview__note">
              <LockIcon size={16} /> Vraag een resetlink aan via je e-mailadres.{' '}
              <Link to={PATHS.auth.forgotPassword}>Wachtwoord resetten</Link>
            </p>
          </div>
        </Card>
      </section>
    </div>
  )
}
